/**
 * Utility for parsing evaluation feedback in various formats
 */

import { formatJSON } from './json-formatter';

/**
 * Parse data that might be in JSON or Java properties format
 * @param data - The raw data string
 * @returns Parsed object or null if parsing fails
 */
export function parseJsonOrProperties(data: string): any {
	try {
		// Try to parse as clean JSON (new format)
		return JSON.parse(data);
	} catch {
		// Handle legacy Java-style properties format: {key=value, key2=value2}
		if (data.startsWith('{') && data.endsWith('}')) {
			try {
				return parseJavaProperties(data);
			} catch {
				console.warn(
					'Failed to parse data in both JSON and properties format:',
					data,
				);
				return null;
			}
		}
		return null;
	}
}

/**
 * Parse Java-style properties format: {key=value, key2=value2}
 * Enhanced to handle complex values with commas and nested structures
 * @param propertiesString - String in Java properties format
 * @returns Parsed object
 */
function parseJavaProperties(propertiesString: string): Record<string, any> {
	const content = propertiesString.slice(1, -1).trim(); // Remove outer braces
	const parsed: Record<string, any> = {};

	let i = 0;
	while (i < content.length) {
		// Skip whitespace and commas
		while (i < content.length && (content[i] === ',' || content[i] === ' ')) {
			i++;
		}
		if (i >= content.length) break;

		// Find the key (look for = sign)
		const equalIndex = content.indexOf('=', i);
		if (equalIndex === -1) break;

		const key = content.slice(i, equalIndex).trim();

		// Find the value - this is the tricky part
		const valueStart = equalIndex + 1;
		let valueEnd = valueStart;
		let depth = 0;
		let inQuotes = false;
		let quoteChar = '';

		while (valueEnd < content.length) {
			const char = content[valueEnd];

			// Handle quoted strings
			if (!inQuotes && (char === '"' || char === "'")) {
				inQuotes = true;
				quoteChar = char;
			} else if (inQuotes && char === quoteChar) {
				inQuotes = false;
				quoteChar = '';
			}

			// Handle nested structures
			if (!inQuotes) {
				if (char === '{' || char === '[') {
					depth++;
				} else if (char === '}' || char === ']') {
					depth--;
				} else if (char === ',' && depth === 0) {
					// Look ahead to see if this is actually the start of a new key=value pair
					let lookahead = valueEnd + 1;
					while (lookahead < content.length && content[lookahead] === ' ') {
						lookahead++;
					}

					// Check if we can find an equals sign before the next comma or end
					let hasEquals = false;
					let tempIndex = lookahead;
					let tempDepth = 0;

					while (tempIndex < content.length) {
						const tempChar = content[tempIndex];
						if (tempChar === '{' || tempChar === '[') {
							tempDepth++;
						} else if (tempChar === '}' || tempChar === ']') {
							tempDepth--;
						} else if (tempChar === '=' && tempDepth === 0) {
							hasEquals = true;
							break;
						} else if (tempChar === ',' && tempDepth === 0) {
							break;
						}
						tempIndex++;
					}

					// Only break if we found a valid key=value pattern ahead
					if (hasEquals) {
						break;
					}
				}
			}

			valueEnd++;
		}

		let rawValue = content.slice(valueStart, valueEnd).trim();

		// Clean up the value
		if (rawValue.startsWith('"') && rawValue.endsWith('"')) {
			rawValue = rawValue.slice(1, -1);
		} else if (rawValue.startsWith("'") && rawValue.endsWith("'")) {
			rawValue = rawValue.slice(1, -1);
		}

		// Try to parse nested structures and convert types
		let value: any = rawValue;

		// Handle nested JSON objects/arrays
		if (rawValue.startsWith('{') || rawValue.startsWith('[')) {
			try {
				value = JSON.parse(rawValue);
			} catch {
				// If JSON parsing fails, try to parse as Java properties if it's an object
				if (rawValue.startsWith('{')) {
					try {
						value = parseJavaProperties(rawValue);
					} catch {
						value = rawValue;
					}
				} else {
					value = rawValue;
				}
			}
		}
		// Handle arrays in string format like "[item1, item2, item3]"
		else if (rawValue.startsWith('[') && rawValue.endsWith(']')) {
			try {
				// Try to parse as JSON array first
				value = JSON.parse(rawValue);
			} catch {
				// If that fails, parse as comma-separated list
				const arrayContent = rawValue.slice(1, -1).trim();
				if (arrayContent) {
					value = arrayContent.split(',').map((item) => item.trim());
				} else {
					value = [];
				}
			}
		}
		// Handle numbers (only if it's clearly a number with no spaces)
		else if (
			!Number.isNaN(Number(rawValue)) &&
			rawValue !== '' &&
			!rawValue.includes(' ')
		) {
			value = Number(rawValue);
		}
		// Handle booleans
		else if (
			rawValue.toLowerCase() === 'true' ||
			rawValue.toLowerCase() === 'false'
		) {
			value = rawValue.toLowerCase() === 'true';
		}

		parsed[key] = value;

		// Move to next key-value pair
		i = valueEnd;
	}

	return parsed;
}

/**
 * Generic function to format any content for display - handles JSON, Java properties, or raw strings
 * @param content - Raw content (string, object, array, etc.)
 * @returns Object with formatted content and whether it was parsed successfully
 */
export function formatContentForDisplay(content: any): {
	content: string;
	isParsed: boolean;
} {
	// If it's already an object or array, format it directly
	if (typeof content === 'object' && content !== null) {
		return {
			content: formatJSON(content),
			isParsed: true,
		};
	}

	// If it's a string, try to parse it
	if (typeof content === 'string') {
		const parsed = parseJsonOrProperties(content);
		if (parsed) {
			return {
				content: formatJSON(parsed),
				isParsed: true,
			};
		}
		// Fallback to raw text
		return {
			content: content,
			isParsed: false,
		};
	}

	// For other types (numbers, booleans, etc.), convert to string
	return {
		content: String(content),
		isParsed: false,
	};
}

/**
 * Legacy function for backward compatibility - redirects to parseJsonOrProperties
 * @param feedback - Raw feedback string
 * @returns Parsed object or null if parsing fails
 */
export function parseFeedback(feedback: string): any {
	return parseJsonOrProperties(feedback);
}

/**
 * Legacy function for backward compatibility - now uses the generic function
 * @param feedback - Raw feedback string
 * @returns Object with formatted content and whether it was parsed successfully
 */
export function formatFeedbackForDisplay(feedback: string): {
	content: string;
	isParsed: boolean;
} {
	return formatContentForDisplay(feedback);
}
